import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate, formatCurrency } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { StatusBadge } from './StatusBadge';
import {
  EnhancedTable,
  StickyTableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
} from './ui/enhanced-table';
import {
  ClipboardList,
  AlertTriangle,
  Clock,
  DollarSign,
  Wrench,
  Timer,
  CheckCircle2,
  TrendingUp,
  RefreshCw,
  ArrowRight,
  Repeat,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  
  const absMinutes = Math.abs(Math.round(minutes));
  
  if (absMinutes >= 1440) {
    const days = Math.floor(absMinutes / 1440);
    const hours = Math.floor((absMinutes % 1440) / 60);
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (absMinutes >= 60) {
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${absMinutes}m`;
}

export default function AssetIntelligenceTab({ assetId }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState('90');
  const [woType, setWoType] = useState('all');
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { days: parseInt(dateRange) };
      if (woType !== 'all') params.wo_type = woType;
      
      const res = await api.get(`/intelligence/assets/${assetId}`, { params });
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch intelligence data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [assetId, dateRange, woType, t]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  if (!data) {
    return (
      <Card>
        <CardContent className="py-8">
          <TableEmptyState
            icon={BarChart3}
            title={t('intelligence.noData')}
            description="No intelligence data available for this asset"
          />
        </CardContent>
      </Card>
    );
  }
  
  const { kpis, trends, reliability, recent_activity } = data;
  
  return (
    <div className="space-y-6" data-testid="asset-intelligence-tab">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[150px]" data-testid="date-range-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">{t('intelligence.last30Days')}</SelectItem>
            <SelectItem value="90">{t('intelligence.last90Days')}</SelectItem>
            <SelectItem value="180">{t('intelligence.last180Days')}</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={woType} onValueChange={setWoType}>
          <SelectTrigger className="w-[150px]" data-testid="wo-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="corrective">{t('sla.corrective')}</SelectItem>
            <SelectItem value="preventive">{t('sla.preventive')}</SelectItem>
          </SelectContent>
        </Select>
        
        <Button variant="ghost" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <ClipboardList className="w-4 h-4" />
              {t('intelligence.totalWOs')}
            </div>
            <p className="text-2xl font-bold mt-1">{kpis.total_wos}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Clock className="w-4 h-4 text-amber-500" />
              {t('intelligence.openWOs')}
            </div>
            <p className="text-2xl font-bold mt-1">{kpis.open_wos}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              {t('intelligence.overdueWOs')}
            </div>
            <p className="text-2xl font-bold mt-1 text-red-600">{kpis.overdue_wos}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <DollarSign className="w-4 h-4 text-green-500" />
              {t('intelligence.totalCost')}
            </div>
            <p className="text-2xl font-bold mt-1">{formatCurrency(kpis.total_cost)}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Timer className="w-4 h-4 text-blue-500" />
              {t('intelligence.avgMTTR')}
            </div>
            <p className="text-2xl font-bold mt-1">
              {kpis.avg_mttr_minutes ? formatDuration(kpis.avg_mttr_minutes) : '—'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <CheckCircle2 className="w-4 h-4 text-brand" />
              {t('intelligence.pmCompliance')}
            </div>
            <p className="text-2xl font-bold mt-1">
              {kpis.pm_compliance_pct !== null ? `${kpis.pm_compliance_pct}%` : '—'}
            </p>
            {kpis.pm_runs_total > 0 && (
              <p className="text-xs text-muted-foreground">
                {kpis.pm_runs_completed}/{kpis.pm_runs_total} runs
              </p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Cost Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('intelligence.totalCost')}: {formatCurrency(kpis.total_cost)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">{t('intelligence.laborCost')}:</span>
              <span className="font-medium ms-1">{formatCurrency(kpis.labor_cost)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('intelligence.partsCost')}:</span>
              <span className="font-medium ms-1">{formatCurrency(kpis.parts_cost)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('intelligence.vendorCost')}:</span>
              <span className="font-medium ms-1">{formatCurrency(kpis.vendor_cost)}</span>
            </div>
            {kpis.total_downtime_minutes !== null && (
              <div>
                <span className="text-muted-foreground">{t('intelligence.totalDowntime')}:</span>
                <span className="font-medium ms-1">{formatDuration(kpis.total_downtime_minutes)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* WOs Over Time */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-brand" />
              {t('intelligence.wosOverTime')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trends.wos_over_time.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={trends.wos_over_time}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="corrective" stackId="a" fill="hsl(var(--destructive))" name="Corrective" />
                  <Bar dataKey="preventive" stackId="a" fill="hsl(var(--brand))" name="Preventive" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
                {t('intelligence.noData')}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Cost Over Time */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-500" />
              {t('intelligence.costOverTime')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trends.cost_over_time.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trends.cost_over_time}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Line type="monotone" dataKey="cost" stroke="hsl(var(--brand))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
                {t('intelligence.noData')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Reliability Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Root Causes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="w-4 h-4 text-amber-500" />
              {t('intelligence.topRootCauses')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reliability.top_root_causes.length > 0 ? (
              <div className="space-y-2">
                {reliability.top_root_causes.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <span className="text-sm truncate flex-1">{item.root_cause}</span>
                    <Badge variant="secondary" className="ms-2">{item.count}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">{t('intelligence.noData')}</p>
            )}
          </CardContent>
        </Card>
        
        {/* Repeat Issues */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Repeat className="w-4 h-4 text-red-500" />
              {t('intelligence.repeatIssues')}
            </CardTitle>
            <CardDescription>Same issue &gt;2 times in 90 days</CardDescription>
          </CardHeader>
          <CardContent>
            {reliability.repeat_issues.length > 0 ? (
              <div className="space-y-3">
                {reliability.repeat_issues.map((item, idx) => (
                  <div key={idx} className="p-3 bg-red-500/5 rounded-lg border border-red-500/10">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{item.root_cause}</span>
                      <Badge variant="destructive">{item.count} {t('intelligence.occurrences')}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('intelligence.lastOccurrence')}: {formatDate(item.last_occurrence)}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.wo_ids.slice(0, 5).map(id => (
                        <Button
                          key={id}
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs font-mono"
                          onClick={() => navigate(`/work-orders/${id}`)}
                        >
                          {id}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No repeat issues detected</p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Failure Categories */}
      {reliability.failure_categories.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('intelligence.failureCategories')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {reliability.failure_categories.map((item, idx) => (
                <Badge key={idx} variant="outline" className="py-1">
                  {t(`failureCategory.${item.category}`, item.category)}
                  <span className="ms-1.5 font-bold">{item.count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {t('intelligence.recentActivity')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <EnhancedTable>
            <StickyTableHeader>
              <tr className="border-b border-border">
                <TableHeader>ID</TableHeader>
                <TableHeader>{t('common.type')}</TableHeader>
                <TableHeader>{t('common.status')}</TableHeader>
                <TableHeader>{t('workOrder.rootCause')}</TableHeader>
                <TableHeader>{t('common.createdAt')}</TableHeader>
                <TableHeader className="text-end">{t('common.actions')}</TableHeader>
              </tr>
            </StickyTableHeader>
            <EnhancedTableBody>
              {recent_activity.length === 0 ? (
                <EnhancedTableRow>
                  <EnhancedTableCell colSpan={6} className="text-center text-muted-foreground py-4">
                    {t('intelligence.noData')}
                  </EnhancedTableCell>
                </EnhancedTableRow>
              ) : (
                recent_activity.map(wo => (
                  <EnhancedTableRow key={wo.wo_id}>
                    <EnhancedTableCell className="font-mono text-xs">{wo.wo_id_short}</EnhancedTableCell>
                    <EnhancedTableCell>
                      <Badge variant={wo.type === 'preventive' ? 'default' : 'secondary'}>
                        {wo.type}
                      </Badge>
                    </EnhancedTableCell>
                    <EnhancedTableCell><StatusBadge status={wo.status} /></EnhancedTableCell>
                    <EnhancedTableCell className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {wo.root_cause || '—'}
                    </EnhancedTableCell>
                    <EnhancedTableCell className="text-sm">{formatDate(wo.created_at)}</EnhancedTableCell>
                    <EnhancedTableCell className="text-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/work-orders/${wo.wo_id}`)}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </EnhancedTableCell>
                  </EnhancedTableRow>
                ))
              )}
            </EnhancedTableBody>
          </EnhancedTable>
        </CardContent>
      </Card>
    </div>
  );
}
